import { Suspense } from 'react';

import { CircularProgress as MuiCircularProgress } from '@mui/material';
import clsx from 'clsx';

import AnimatedPageLoaderGraphic from '@components/graphics/AnimatedPageLoaderGraphic';

interface IPageLoadingProps {
    className?: string;
    label?: string;
    /**
     * Fills the viewport and centers the loader both horizontally and
     * vertically. Defaults to `true` since most loaders are full-page /
     * route-level states; pass `fullPage={false}` when embedding inside a
     * modal, popover, or other smaller container.
     */
    fullPage?: boolean;
}

// MUI applies the circle color via the nested svg, so target it directly.
const spinnerClassName = '[&_svg]:text-cerulean-600';

const PageLoading = ({
    className = undefined,
    label = undefined,
    fullPage = true,
}: IPageLoadingProps) => {
    return (
        <div
            className={clsx(
                'page-loading text-center',
                fullPage &&
                    'pointer-events-none fixed inset-0 flex flex-col items-center justify-center',
                className,
            )}
        >
            <div className="flex min-h-12 items-center justify-center">
                {fullPage ? (
                    // The branded Lottie loader is reserved for full-page states.
                    // Embedded loaders fall back to the lightweight MUI spinner,
                    // which also keeps `lottie-web` out of those code paths.
                    <Suspense fallback={<MuiCircularProgress className={spinnerClassName} />}>
                        <AnimatedPageLoaderGraphic />
                    </Suspense>
                ) : (
                    <MuiCircularProgress className={spinnerClassName} />
                )}
            </div>
            <div className="mt-3 text-[15px] font-medium leading-[1.4] tracking-[0.01em] text-gray-700">
                {label}
            </div>
        </div>
    );
};

export default PageLoading;
